import React, { useEffect, useState } from "react";
import moment from "moment";
import { useSiteContext } from "../../contexts/SiteProvider";
import { SdsForm } from "soft_digi";
import { hubs_get_all_select } from "../../services/site/SelectBoxServices";
import { yupResolver } from "@hookform/resolvers/yup";
import { METER_READINGS_URLS } from "../../api/UserUrls";
import { post } from "../../services/smartApiService";
import { showAlertAutoClose } from "../../services/notifyService";
import { useForm, useWatch } from "react-hook-form";
import * as Yup from "yup";
import { changeDateTimeZoneFormat } from "../../services/core/CommonService";
import { SdsFormElementProps } from "soft_digi/dist/sdforms/SdsFromInterFace";

interface MeterReadingFormData {
  ID?: number;
  sd_hub_id: Object;
  meter_start_date: string;
  meter_end_date: string;
  meter_start: number;
  meter_end: number;
  numberof_unit?: number;
  meter_cost?: number;
}

interface HeaderProps {
  loadTableData: () => void;
  dataIn?: MeterReadingFormData;
}

const getDefaultValues = (): MeterReadingFormData => ({
  sd_hub_id: '',
  meter_start_date: moment().format("YYYY-MM-DD"),  // auto-select today
  meter_end_date: '',
  meter_start: 0,
  meter_end: 0,
  numberof_unit: 0,
  meter_cost: 0,
});

const schema = Yup.object({
  sd_hub_id: Yup.object().required('Hub is required'),
  meter_start_date: Yup.string().required('Start date is required'),
  meter_end_date: Yup.string()
    .required('End date is required')
 ,
  meter_start: Yup.number()
    .typeError('Start reading must be a number')
    .required('Start reading is required')
    .min(0, 'Start reading cannot be negative'),
  meter_end: Yup.number()
    .typeError('End reading must be a number')
    .required('End reading is required')
    .min(Yup.ref('meter_start'), 'End reading must be greater than start reading'),
  meter_cost: Yup.number()
    .typeError('Cost must be a number')
    .optional()
    .min(0, 'Cost cannot be negative'),
});

const MeterReadingForm: React.FC<HeaderProps> = ({ dataIn, loadTableData }) => {
  // Merge caller-supplied values over fresh defaults so today's date is
  // always the starting value for new entries, even when hub is pre-filled.
  const resolvedData: MeterReadingFormData = {
    ...getDefaultValues(),
    ...dataIn,
    // Keep today's date if caller passed an empty string
    meter_start_date: dataIn?.meter_start_date || moment().format("YYYY-MM-DD"),
  };
  const { closeModal } = useSiteContext();
  const [allHubs, setAllHubs] = useState<{value: string, label: string}[]>([]);
  const [minEndDate, setMinEndDate] = useState<string | undefined>();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<MeterReadingFormData>({
    defaultValues: resolvedData,
    resolver: yupResolver(schema),
  });

  const [meterStart, meterEnd] = useWatch({
    control,
    name: ['meter_start', 'meter_end']
  });

  useEffect(() => {
    if (meterStart !== undefined && meterEnd !== undefined) {
      const units = meterEnd - meterStart;
      setValue('numberof_unit', units >= 0 ? units : 0);
    }
  }, [meterStart, meterEnd, setValue]);

  const handleStartDateChange = (date: string) => {
    setMinEndDate(date);
  };

  const onSubmit = async (data: MeterReadingFormData) => {
    try {
      const url = data.ID ? METER_READINGS_URLS.UPDATE : METER_READINGS_URLS.INSERT;
      const payload = {
        ...data,
        ...(data.ID && { id: data.ID }),
        meter_start_date: changeDateTimeZoneFormat(data.meter_start_date, "YYYY-MM-DD"),
        meter_end_date: changeDateTimeZoneFormat(data.meter_end_date, "YYYY-MM-DD"),
      };

      const subscription = post(url, payload).subscribe({
        next: () => {
          loadTableData();
          showAlertAutoClose("Data Saved Successfully", "success");
          closeModal();
        },
        error: (error) => {
          showAlertAutoClose("Failed to save data", "error");
          console.error("Submission error:", error);
        }
      });

      return () => subscription.unsubscribe();
    } catch (error) {
      showAlertAutoClose("An error occurred", "error");
      console.error("Error in form submission:", error);
    }
  };

  useEffect(() => {
    hubs_get_all_select((data: any) => setAllHubs(data));
  }, []);

  const formElements: SdsFormElementProps[] = [
    {
      type: "SELECT_BOX",
      width: 12,
      name: "sd_hub_id",
      element: {
        label: "Hub Name",
        isRequired: true,
        options: allHubs,
        inputType: "BORDER_LABEL",
      },
    },
    {
      type: "DATE",
      width: 12,
      name: "meter_start_date",
      element: {
        label: "Start Date",
        isRequired: true,
        inputType: "BORDER_LABEL",
        onChange: handleStartDateChange,
      },
    },
    {
      type: "DATE",
      width: 12,
      name: "meter_end_date",
      element: {
        label: "End Date",
        isRequired: true,
        inputType: "BORDER_LABEL",
        minDate: minEndDate,
      },
    },
    {
      type: "TEXT_BOX",
      width: 12,
      name: "meter_start",
      element: {
        label: "Meter Start Reading",
        isRequired: true,
        inputType: "BORDER_LABEL",
     
      },
    },
    {
      type: "TEXT_BOX",
      width: 12,
      name: "meter_end",
      element: {
        label: "Meter End Reading",
        isRequired: true,
        inputType: "BORDER_LABEL",
       
      },
    },
    {
      type: "TEXT_BOX",
      width: 12,
      name: "numberof_unit",
      element: {
        label: "Number of Units",
        inputType: "BORDER_LABEL",
        disabled: true,
        value: meterEnd !== undefined && meterStart !== undefined 
          ? Math.max(0, meterEnd - meterStart).toString() 
          : "0",
      },
    },
    {
      type: "TEXT_BOX",
      width: 12,
      name: "meter_cost",
      element: {
        label: "Meter Cost",
        inputType: "BORDER_LABEL",
    
      },
    },
    {
      type: "LABEL",
      width: 8,
      name: "spacer",
    },
    {
      type: "BUTTON",
      width: 2,
      name: "cancel_button",
      class_name: "has-text-right",
      element: {
        classList: ["button", "smart-third-button"],
        label: "Close",
        onClick: closeModal,
      },
    },
    {
      type: "BUTTON",
      width: 2,
      name: "submit_button",
      element: {
        classList: ["button", "smart-action-button"],
        label: resolvedData?.ID ? "Update" : "Add",
        onClick: handleSubmit(onSubmit),
      },
    },
  ];

  return (
    <div className="sd-efl-input">
      <SdsForm elements={formElements} errors={errors} control={control} />
    </div>
  );
};

export default MeterReadingForm;